import { useEffect, useMemo, useRef } from "react";
import debounce from "lodash/debounce";
import { reverseGeocode } from "@/services/geocodingService";
import {
  formatPlaceLabel,
  isGeoFilter,
} from "@/components/UI/TableToolbar/Filtering/geoFilterUtil";
import type { GeoFilterCondition } from "@/components/UI/TableToolbar/Filtering/filterTypes";

const GEO_FILTER_UPDATE_DEBOUNCE_MS = 300;

type UseGeoRadiusFilterParams = {
  /** Field name of the geo filter in the screen's global filters. */
  geoField: string;
  filters: Record<string, any>;
  setFilters?: (filters: Record<string, any>) => void;
};

/**
 * Reads and writes the radius filter through the screen's global filter state
 * — the same path the filter popover uses — so the table, plots, chips,
 * workspaces and exports all inherit it automatically. Also resolves a
 * reverse-geocoded place label for the chip, patched in asynchronously.
 */
export const useGeoRadiusFilter = ({
  geoField,
  filters,
  setFilters,
}: UseGeoRadiusFilterParams) => {
  const geoCondition: GeoFilterCondition | null = isGeoFilter(filters[geoField])
    ? filters[geoField][0]
    : null;

  // Refs keep debounced/async writers reading the latest state without
  // re-creating the debounce on every filter change.
  const filtersRef = useRef(filters);
  const setFiltersRef = useRef(setFilters);
  const geoFieldRef = useRef(geoField);
  useEffect(() => {
    filtersRef.current = filters;
    setFiltersRef.current = setFilters;
    geoFieldRef.current = geoField;
  });

  const writeGeoFilter = (condition: GeoFilterCondition) => {
    setFiltersRef.current?.({
      ...filtersRef.current,
      [geoFieldRef.current]: [condition],
    });
  };

  const resolveLabel = async (condition: GeoFilterCondition) => {
    const label = formatPlaceLabel(
      await reverseGeocode(condition.lat, condition.lng),
    );
    if (!label) return;
    // Only patch the label if the filter still points at the same center.
    const current = filtersRef.current[geoFieldRef.current];
    if (
      isGeoFilter(current) &&
      current[0].lat === condition.lat &&
      current[0].lng === condition.lng
    ) {
      writeGeoFilter({ ...current[0], label });
    }
  };

  const applyWithLabel = (condition: GeoFilterCondition) => {
    writeGeoFilter(condition);
    if (!condition.label) void resolveLabel(condition);
  };

  const debouncedUpdate = useMemo(
    () =>
      debounce(
        (condition: GeoFilterCondition) => applyWithLabel(condition),
        GEO_FILTER_UPDATE_DEBOUNCE_MS,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- writers read refs
    [],
  );
  useEffect(() => () => debouncedUpdate.cancel(), [debouncedUpdate]);

  const applyGeoFilter = (condition: GeoFilterCondition) => {
    debouncedUpdate.cancel();
    applyWithLabel(condition);
  };

  /** Debounced (~300ms) variant for pin drags and radius input changes. */
  const updateGeoFilter = (condition: GeoFilterCondition) => {
    debouncedUpdate(condition);
  };

  const clearGeoFilter = () => {
    debouncedUpdate.cancel();
    setFiltersRef.current?.({
      ...filtersRef.current,
      [geoFieldRef.current]: [],
    });
  };

  return { geoCondition, applyGeoFilter, updateGeoFilter, clearGeoFilter };
};
